"use client";

import React, { useEffect, useState, useCallback } from "react";
import { OrderListItem } from "../services/order-service";
import OrderEmployeeService from "../services/order-employee-service";
import { WageRatesService } from "../../employee-wage-rates/services/wage-rates-service";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import { formatKg } from "../../../utils/helpers";

interface EmployeeWeightData {
  id: number;
  employee_id: number;
  role_type: number;
  employee_name: string;
  assigned_weight: string;
  wage_rate: string;
  calculated_wage: number;
  status: number;
  loadingRate?: boolean;
}

interface OrderEmployeeWeightModalProps {
  open: boolean;
  order: OrderListItem | null;
  onClose: () => void;
  onSaved?: () => void;
}

const ROLE_LABELS: Record<number, string> = {
  1: "Lấy hàng",
  2: "Giặt",
  3: "Giao hàng",
  4: "Kiểm tra",
  5: "Giặt",
  6: "Sấy",
  7: "Ủi",
  8: "Gấp",
  9: "Kiểm đếm",
  10: "Trung chuyển hàng dơ",
  11: "Trung chuyển hàng sạch",
  12: "Kiểm đếm giao",
};

const ROLE_COLORS: Record<number, string> = {
  1: "bg-blue-100 text-blue-700",
  2: "bg-purple-100 text-purple-700",
  3: "bg-green-100 text-green-700",
  4: "bg-cyan-100 text-cyan-700",
  5: "bg-indigo-100 text-indigo-700",
  6: "bg-orange-100 text-orange-700",
  7: "bg-rose-100 text-rose-700",
  8: "bg-teal-100 text-teal-700",
  9: "bg-violet-100 text-violet-700",
  10: "bg-amber-100 text-amber-700",
  11: "bg-sky-100 text-sky-700",
  12: "bg-violet-100 text-violet-700",
};

/** Số ký → đơn vị 0,01 kg (số nguyên) — so sánh / cộng không lệch số thực. Khớp `toCents` của BE (weight-split.ts). */
const toCents = (kg: number | string): number => Math.round((Number(kg) || 0) * 100);

/** Chia `totalCents` cho `n` người, tổng các phần = đúng `totalCents` (0,01 kg lẻ dồn cho người đầu). */
const splitEvenCents = (totalCents: number, n: number): number[] => {
  if (n <= 0) return [];
  const base = Math.floor(totalCents / n);
  const extra = totalCents - base * n;
  return Array.from({ length: n }, (_, i) => base + (i < extra ? 1 : 0));
};

const formatCurrency = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return num.toLocaleString("vi-VN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const OrderEmployeeWeightModal: React.FC<OrderEmployeeWeightModalProps> = ({
  open,
  order,
  onClose,
  onSaved,
}) => {
  const [employees, setEmployees] = useState<EmployeeWeightData[]>([]);
  const [saving, setSaving] = useState<number | null>(null);
  const [savingAll, setSavingAll] = useState<number | null>(null);
  const { fetch: updateWeight } = OrderEmployeeService.updateWeight();
  const { fetch: distributeWeight, loading: distributing } = OrderEmployeeService.distributeWeight();
  const { fetch: getApplicableWageRate } = WageRatesService.getApplicable();

  // Fetch wage rate for a single employee
  const fetchSingleWageRate = async (
    emp: { employee_id: number; role_type: number; id: number },
    branchId: number
  ): Promise<number> => {
    try {
      const res = await getApplicableWageRate({
        role_type: emp.role_type,
        employee_id: emp.employee_id,
        branch_id: branchId,
      });
      if (res && isSuccess(res) && res.data) {
        return res.data.rate_per_kg || 0;
      }
    } catch {
      // ignore
    }
    return 0;
  };

  // Sync data and auto-fetch wage rates when modal opens
  useEffect(() => {
    if (open && order?.employees) {
      // Bỏ người đã bị gỡ (REJECTED = 3): không được chia kg, và nếu còn trong bảng thì tổng của vai bị lệch.
      const empData: EmployeeWeightData[] = order.employees
        .filter((emp) => Number(emp.status) !== 3)
        .map((emp) => ({
        id: emp.id,
        employee_id: emp.employee_id,
        role_type: emp.role_type,
        employee_name: emp.employee?.name || `NV #${emp.employee_id}`,
        assigned_weight: emp.assigned_weight?.toString() || "0",
        wage_rate: emp.wage_rate?.toString() || "0",
        calculated_wage: Number(emp.calculated_wage) || 0,
        status: emp.status,
        loadingRate: !emp.wage_rate || emp.wage_rate === 0, // Mark as loading if no rate
      }));
      setEmployees(empData);

      // Auto-fetch wage rates for employees without one
      const fetchAllRates = async () => {
        const branchId = order.branch_id || 0;
        const updates: { id: number; wage_rate: number }[] = [];

        for (const emp of (order.employees || []).filter((e) => Number(e.status) !== 3)) {
          if (!emp.wage_rate || emp.wage_rate === 0) {
            const rate = await fetchSingleWageRate(emp, branchId);
            updates.push({ id: emp.id, wage_rate: rate });
          }
        }

        if (updates.length > 0) {
          setEmployees((prev) =>
            prev.map((e) => {
              const found = updates.find((u) => u.id === e.id);
              if (found) {
                const weight = parseFloat(e.assigned_weight) || 0;
                return {
                  ...e,
                  wage_rate: found.wage_rate.toString(),
                  calculated_wage: weight * found.wage_rate,
                  loadingRate: false,
                };
              }
              return { ...e, loadingRate: false };
            })
          );
        } else {
          setEmployees((prev) => prev.map((e) => ({ ...e, loadingRate: false })));
        }
      };

      fetchAllRates();
    }
  }, [open, order?.id, order?.employees, order?.branch_id]);

  if (!open || !order) return null;

  const orderWeight = Number(order.final_weight) || Number(order.pickup_weight) || 0;

  // Fetch wage rate for employee (manual refresh)
  const fetchWageRate = async (emp: EmployeeWeightData) => {
    setEmployees((prev) =>
      prev.map((e) => (e.id === emp.id ? { ...e, loadingRate: true } : e))
    );

    try {
      const res = await getApplicableWageRate({
        role_type: emp.role_type,
        employee_id: emp.employee_id,
        branch_id: order.branch_id || 0,
      });

      if (res && isSuccess(res) && res.data) {
        const ratePerKg = res.data.rate_per_kg || 0;
        setEmployees((prev) =>
          prev.map((e) =>
            e.id === emp.id
              ? {
                  ...e,
                  wage_rate: ratePerKg.toString(),
                  calculated_wage: (parseFloat(e.assigned_weight) || 0) * ratePerKg,
                  loadingRate: false,
                }
              : e
          )
        );
      } else {
        setEmployees((prev) =>
          prev.map((e) => (e.id === emp.id ? { ...e, loadingRate: false } : e))
        );
      }
    } catch {
      setEmployees((prev) =>
        prev.map((e) => (e.id === emp.id ? { ...e, loadingRate: false } : e))
      );
    }
  };

  const handleWeightChange = (id: number, value: string, autoRedistribute = true) => {
    setEmployees((prev) => {
      const changedEmp = prev.find((e) => e.id === id);
      if (!changedEmp) return prev;

      const newWeight = parseFloat(value) || 0;
      const roleType = changedEmp.role_type;
      const sameRoleOthers = prev.filter((e) => e.role_type === roleType && e.id !== id);

      // Nếu có orderWeight và autoRedistribute → chia phần còn lại cho các NV cùng vai trò
      if (autoRedistribute && orderWeight > 0 && sameRoleOthers.length > 0) {
        // Phần còn lại chia cho người cùng vai, tổng KHỚP ĐÚNG (dồn 0,01 kg lẻ cho người đầu) — không làm tròn từng
        // phần (VD còn 90 kg / 7 người → 12.86 × 7 = 90.02 → lệch, lưu được nhưng BE chặn hoàn tất).
        const remainingCents = Math.max(0, toCents(orderWeight) - toCents(newWeight));
        const shares = splitEvenCents(remainingCents, sameRoleOthers.length);
        const shareById = new Map(sameRoleOthers.map((e, i) => [e.id, shares[i] / 100] as const));

        return prev.map((emp) => {
          if (emp.id === id) {
            return {
              ...emp,
              assigned_weight: value,
              calculated_wage: newWeight * (parseFloat(emp.wage_rate) || 0),
            };
          }
          const share = shareById.get(emp.id);
          if (share !== undefined) {
            return {
              ...emp,
              assigned_weight: share.toString(),
              calculated_wage: share * (parseFloat(emp.wage_rate) || 0),
            };
          }
          return emp;
        });
      }

      // Fallback: chỉ thay đổi NV hiện tại
      return prev.map((emp) =>
        emp.id === id
          ? {
              ...emp,
              assigned_weight: value,
              calculated_wage: newWeight * (parseFloat(emp.wage_rate) || 0),
            }
          : emp
      );
    });
  };

  const handleSaveWeight = async (emp: EmployeeWeightData) => {
    // Validate total weight khớp với số ký đơn hàng
    const currentRole = emp.role_type;
    const roleEmployees = employees.filter((e) => e.role_type === currentRole);
    const roleTotal = roleEmployees.reduce((sum, e) => sum + (parseFloat(e.assigned_weight) || 0), 0);

    // Khớp đúng tới 0,01 kg (BE chặn hoàn tất nếu lệch) — "Chia đều" / tự chia đã dồn phần lẻ nên luôn khớp.
    if (orderWeight > 0 && toCents(roleTotal) !== toCents(orderWeight)) {
      showToast({
        message: `Tổng số ký ${ROLE_LABELS[currentRole]} (${roleTotal.toFixed(2)}kg) không khớp số ký đơn hàng (${orderWeight}kg). Vui lòng điều chỉnh lại.`,
        type: "error",
      });
      return;
    }

    setSaving(emp.id);
    try {
      const res = await updateWeight(emp.id, {
        assigned_weight: parseFloat(emp.assigned_weight) || 0,
      });
      if (res && isSuccess(res)) {
        const updatedData = res.data;
        if (updatedData) {
          setEmployees((prev) =>
            prev.map((e) =>
              e.id === emp.id
                ? {
                    ...e,
                    wage_rate: updatedData.wage_rate?.toString() || e.wage_rate,
                    calculated_wage: Number(updatedData.calculated_wage) || e.calculated_wage,
                  }
                : e
            )
          );
        }
        showToast({ message: `Đã lưu số ký cho ${emp.employee_name}`, type: "success" });
        onSaved?.();
      } else {
        showToast({ message: res?.message || "Không thể lưu", type: "error" });
      }
    } catch {
      showToast({ message: "Có lỗi khi lưu", type: "error" });
    } finally {
      setSaving(null);
    }
  };

  // Lưu tất cả NV của 1 vai trò (sau khi auto-redistribute)
  const handleSaveAllWeights = async (roleType: number) => {
    const roleEmployees = employees.filter((e) => e.role_type === roleType);
    const roleTotal = roleEmployees.reduce((sum, e) => sum + (parseFloat(e.assigned_weight) || 0), 0);

    if (orderWeight > 0 && toCents(roleTotal) !== toCents(orderWeight)) {
      showToast({
        message: `Tổng số ký ${ROLE_LABELS[roleType]} (${roleTotal.toFixed(2)}kg) không khớp số ký đơn hàng (${orderWeight}kg)`,
        type: "error",
      });
      return;
    }

    setSavingAll(roleType);
    let savedCount = 0;
    try {
      for (const emp of roleEmployees) {
        const res = await updateWeight(emp.id, {
          assigned_weight: parseFloat(emp.assigned_weight) || 0,
        });
        if (res && isSuccess(res)) {
          const updatedData = res.data;
          if (updatedData) {
            setEmployees((prev) =>
              prev.map((e) =>
                e.id === emp.id
                  ? {
                      ...e,
                      wage_rate: updatedData.wage_rate?.toString() || e.wage_rate,
                      calculated_wage: Number(updatedData.calculated_wage) || e.calculated_wage,
                    }
                  : e
              )
            );
          }
          savedCount++;
        }
      }
      showToast({ message: `Đã lưu ${savedCount}/${roleEmployees.length} NV ${ROLE_LABELS[roleType]}`, type: "success" });
      onSaved?.();
    } catch {
      showToast({ message: "Có lỗi khi lưu", type: "error" });
    } finally {
      setSavingAll(null);
    }
  };

  const handleDistributeWeight = async (roleType: number) => {
    const res = await distributeWeight(order.id, roleType);
    console.log('[DEBUG] distributeWeight response:', res);
    if (res && isSuccess(res)) {
      showToast({ message: "Đã chia đều số ký", type: "success" });
      onSaved?.();
      const responseData = res.data;
      const assignments = (responseData as any)?.assignments;
      console.log('[DEBUG] assignments:', assignments);
      if (assignments && Array.isArray(assignments)) {
        const updated = assignments as Array<{
          id: number;
          assigned_weight: number;
          wage_rate: number;
          calculated_wage: number;
        }>;
        setEmployees((prev) =>
          prev.map((emp) => {
            const found = updated.find((u) => u.id === emp.id);
            if (found) {
              return {
                ...emp,
                assigned_weight: found.assigned_weight.toString(),
                wage_rate: found.wage_rate?.toString() || emp.wage_rate,
                calculated_wage: Number(found.calculated_wage) || 0,
              };
            }
            return emp;
          })
        );
      }
    } else {
      showToast({ message: res?.message || "Không thể chia đều", type: "error" });
    }
  };

  // Group employees by role
  const employeesByRole = employees.reduce((acc, emp) => {
    if (!acc[emp.role_type]) acc[emp.role_type] = [];
    acc[emp.role_type].push(emp);
    return acc;
  }, {} as Record<number, EmployeeWeightData[]>);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[900px] max-w-[95vw] p-6 shadow-2xl animate-fadeIn max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">
              Phân công số ký cho nhân viên
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Đơn hàng: <span className="font-medium">{order.code || `OD-${order.id}`}</span>
              {orderWeight > 0 && (
                <span className="ml-2 text-blue-600 font-semibold">
                  ({orderWeight} kg)
                </span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 text-lg"
          >
            ✕
          </button>
        </div>

        {/* Weight Summary */}
        <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-100">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs text-slate-500">Số ký khi nhận (cân lần 1)</div>
              <div className="text-lg font-semibold text-blue-600">
                {Number(order.pickup_weight) > 0 ? `${formatKg(order.pickup_weight)} kg` : "Chưa cân"}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Số ký sau giặt (cân lần 2 — tính tiền)</div>
              <div className="text-lg font-semibold text-green-600">
                {Number(order.final_weight) > 0 ? `${formatKg(order.final_weight)} kg` : "Chưa cân"}
              </div>
            </div>
          </div>
        </div>

        {/* Employee Groups */}
        {employees.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            Chưa có nhân viên được phân công
          </div>
        ) : (
          <div className="space-y-6">
            {/* Mọi vai có sản lượng, theo TRÌNH TỰ công việc: kiểm đếm nhận → lấy hàng → trung chuyển dơ → kiểm
                tra → giặt/sấy/ủi/gấp → trung chuyển sạch → giao hàng → kiểm đếm giao. Kiểm đếm (9/12) và trung
                chuyển (10/11) cho chia tay từ 2026-09-28 — mỗi vai = kg đơn, chia trong nội bộ vai. */}
            {[9, 1, 10, 4, 2, 5, 6, 7, 8, 11, 3, 12].map((roleType) => {
              const roleEmployees = employeesByRole[roleType];
              if (!roleEmployees || roleEmployees.length === 0) return null;

              const totalAssigned = roleEmployees.reduce(
                (sum, e) => sum + (parseFloat(e.assigned_weight) || 0),
                0
              );
              const totalWage = roleEmployees.reduce(
                (sum, e) => sum + (Number(e.calculated_wage) || 0),
                0
              );
              // So theo đơn vị 0,01 kg, KHỚP ĐÚNG — BE chặn hoàn tất nếu tổng vai giao lệch dù chỉ 0,01 kg.
              const isOverWeight = orderWeight > 0 && toCents(totalAssigned) > toCents(orderWeight);
              const isUnderWeight = orderWeight > 0 && toCents(totalAssigned) < toCents(orderWeight);
              const isMismatch = isOverWeight || isUnderWeight;

              return (
                <div key={roleType} className="border rounded-lg overflow-hidden">
                  <div className={`px-4 py-2 ${ROLE_COLORS[roleType]} flex items-center justify-between`}>
                    <div className="font-semibold">
                      {ROLE_LABELS[roleType]} ({roleEmployees.length} người)
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <span className={isMismatch ? "text-red-600 font-bold" : "text-green-700 font-semibold"}>
                        Tổng: {formatKg(totalAssigned)} kg
                        {orderWeight > 0 && ` / ${formatKg(orderWeight)} kg`}
                        {isOverWeight && " ⚠ Vượt!"}
                        {isUnderWeight && " ⚠ Thiếu!"}
                        {!isMismatch && orderWeight > 0 && " ✓"}
                      </span>
                      {/* <span>Công: {formatCurrency(totalWage)}đ</span> */}
                      <button
                        onClick={() => handleDistributeWeight(roleType)}
                        disabled={distributing}
                        className="px-2 py-1 bg-white rounded text-xs hover:bg-slate-100 disabled:opacity-50"
                        title="Chia đều số ký cho tất cả nhân viên"
                      >
                        Chia đều
                      </button>
                      <button
                        onClick={() => handleSaveAllWeights(roleType)}
                        disabled={savingAll === roleType || isMismatch}
                        className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                        title={isMismatch ? "Tổng số ký chưa khớp, không thể lưu" : "Lưu tất cả nhân viên"}
                      >
                        {savingAll === roleType ? "Đang lưu..." : "Lưu tất cả"}
                      </button>
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-4 py-2 text-left">Nhân viên</th>
                        <th className="px-4 py-2 text-center w-[120px]">Số ký</th>
                        {/* <th className="px-4 py-2 text-center w-[140px]">Đơn giá (đ/kg)</th> */}
                        {/* <th className="px-4 py-2 text-right w-[140px]">Tiền công</th> */}
                        <th className="px-4 py-2 text-center w-[80px]"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {roleEmployees.map((emp) => (
                        <tr key={emp.id} className="hover:bg-slate-50">
                          <td className="px-4 py-2 font-medium text-slate-800">
                            {emp.employee_name}
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="number"
                              step="0.1"
                              min="0"
                              value={emp.assigned_weight}
                              onChange={(e) => handleWeightChange(emp.id, e.target.value)}
                              className="w-full border rounded px-2 py-1 text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          {/* Ẩn cột đơn giá và tiền công */}
                          {/* <td className="px-4 py-2 text-center">
                            {emp.loadingRate ? (
                              <span className="text-slate-400 text-xs">Đang lấy...</span>
                            ) : parseFloat(emp.wage_rate) > 0 ? (
                              <span className="text-blue-600 font-medium">
                                {formatCurrency(parseFloat(emp.wage_rate))}đ/kg
                              </span>
                            ) : (
                              <span className="text-slate-400 text-xs">Chưa có</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right font-semibold text-green-600">
                            {formatCurrency(emp.calculated_wage)}đ
                          </td> */}
                          <td className="px-4 py-2 text-center">
                            <button
                              onClick={() => handleSaveWeight(emp)}
                              disabled={saving === emp.id}
                              className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {saving === emp.id ? "..." : "Lưu"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {isMismatch && orderWeight > 0 && (
                    <div className="px-4 py-2 bg-red-50 border-t border-red-200 text-red-600 text-xs">
                      ⚠ Tổng số ký ({totalAssigned.toFixed(2)} kg) chưa khớp với số ký đơn hàng ({orderWeight} kg).
                      {isOverWeight && ` Vượt ${formatKg(totalAssigned - orderWeight)} kg.`}
                      {isUnderWeight && ` Thiếu ${(orderWeight - totalAssigned).toFixed(2)} kg.`}
                      {" "}Vui lòng điều chỉnh hoặc bấm "Chia đều".
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 border rounded-lg text-slate-700 hover:bg-slate-100"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderEmployeeWeightModal;
